import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/services/api';
import { downloadCSV } from '@/utils/csv';
import { HiOutlineDocumentDownload, HiOutlineRefresh } from 'react-icons/hi';

interface Invoice {
  id: string;
  invoiceType: string;
  targetYear: number;
  targetMonth: number;
  totalDays: number;
  totalUnits: number;
  totalAmount: string;
  status: string;
  createdAt: string;
}

const statusLabels: Record<string, { label: string; class: string }> = {
  draft: { label: '下書き', class: 'badge-gray' },
  calculated: { label: '計算済', class: 'badge-primary' },
  confirmed: { label: '確定', class: 'badge-success' },
  submitted: { label: '送信済', class: 'badge-success' },
  completed: { label: '完了', class: 'badge-success' },
  error: { label: 'エラー', class: 'badge-danger' },
};

export default function InvoicesPage() {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [downloading, setDownloading] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<{ data: Invoice[] }>({
    queryKey: ['invoices', { year, month }],
    queryFn: () => api.get(`/api/v1/invoices?year=${year}&month=${month}`) as Promise<{ data: Invoice[] }>,
  });

  const generateMutation = useMutation({
    mutationFn: () =>
      api.post('/api/v1/invoices/generate', {
        targetYear: year,
        targetMonth: month,
        invoiceType: 'national_health',
      }),
    onSuccess: () => {
      toast.success('請求データを生成しました');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: () => {
      toast.error('請求データの生成に失敗しました');
    },
  });

  const handleDownload = async (invoice: Invoice) => {
    setDownloading(invoice.id);
    try {
      await downloadCSV(`/api/v1/export/invoices/${invoice.id}`);
      toast.success('ダウンロードしました');
    } catch (error) {
      toast.error('ダウンロードに失敗しました');
    } finally {
      setDownloading(null);
    }
  };

  const invoices = data?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">請求管理</h1>
          <p className="text-gray-600 mt-1">国保連請求・利用者請求を管理します</p>
        </div>
        <button
          onClick={() => generateMutation.mutate()}
          disabled={generateMutation.isPending}
          className="btn-primary"
        >
          <HiOutlineRefresh className={`w-5 h-5 mr-2 ${generateMutation.isPending ? 'animate-spin' : ''}`} />
          {generateMutation.isPending ? '生成中...' : '請求データ生成'}
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-4">
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="input max-w-[120px]"
            >
              {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                <option key={y} value={y}>{y}年</option>
              ))}
            </select>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              className="input max-w-[100px]"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{m}月</option>
              ))}
            </select>
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>対象月</th>
                <th>請求種別</th>
                <th>合計日数</th>
                <th>合計単位</th>
                <th>合計金額</th>
                <th>状態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    読み込み中...
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    請求データがありません
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{invoice.targetYear}年{invoice.targetMonth}月</td>
                    <td>{invoice.invoiceType === 'national_health' ? '国保連' : invoice.invoiceType}</td>
                    <td>{invoice.totalDays}日</td>
                    <td>{(invoice.totalUnits || 0).toLocaleString()}単位</td>
                    <td className="font-medium">{parseInt(invoice.totalAmount || '0').toLocaleString()}円</td>
                    <td>
                      <span className={statusLabels[invoice.status]?.class || 'badge-gray'}>
                        {statusLabels[invoice.status]?.label || invoice.status}
                      </span>
                    </td>
                    <td>
                      <button 
                        onClick={() => handleDownload(invoice)}
                        disabled={downloading === invoice.id}
                        className="btn-ghost text-sm py-1 px-2"
                      >
                        <HiOutlineDocumentDownload className={`w-4 h-4 mr-1 ${downloading === invoice.id ? 'animate-pulse' : ''}`} />
                        {downloading === invoice.id ? 'ダウンロード中...' : 'ダウンロード'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
